import { useMemo } from "react";
import { FiShoppingCart } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useCountBuyerEventsByStatusQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import {
  BUCKET_META,
  bucketQuery,
  countBadgesByStatus,
  type BucketKey,
} from "../../../common/layout/statusBuckets";
import { useNavLabel } from "../../../common/layout/useNavLabel";
import { useSub } from "../../../common/subscription/useSub";
import { urls } from "../../../urls";
import { DoughnutBreakdown } from "./DoughnutBreakdown";
import { PanelBody, PanelCard, PanelHeader } from "./HomePanel";

/** The lifecycle, left to right. Drafts at one end, finished and then cancelled at the other. */
const PHASES: BucketKey[] = ["arbeid", "kunngjort", "evaluering", "tildeling", "fullfort", "avlyst"];

/** Identity tokens for the six phases, in lifecycle order — see their definition in config.ts. */
const PHASE_COLOR: Record<BucketKey, string> = {
  arbeid: "smChartPhaseDraft",
  kunngjort: "smChartPhaseAnnounced",
  evaluering: "smChartPhaseEvaluation",
  tildeling: "smChartPhaseAward",
  fullfort: "smChartPhaseCompleted",
  avlyst: "smChartPhaseCancelled",
};

/**
 * The organization's procurements by phase, as the counterpart to the agreements-expiry chart. The
 * counts come from the query the sidebar badges already run, so the slices agree with the menu and
 * the phase strip in the Procurements card, and cost no extra request.
 */
export const ProcurementPhasesPanel = () => {
  const navigate = useNavigate();
  const navLabel = useNavLabel();
  const sub = useSub();
  const authState = useLoggedInWithOrgContextState();

  const { data, isLoading, isError } = useCountBuyerEventsByStatusQuery(
    (sub.hasBuyerAccess ? sub.canViewSourcingEvents : sub.viewsSourcingEvents) ? { orgId: authState.selectedOrg.id } : skipToken
  );

  const badges = useMemo(() => countBadgesByStatus(data?.counts ?? []), [data]);
  const counts = PHASES.map((phase) => badges[BUCKET_META[phase].badgeKey] ?? 0);
  const total = counts.reduce((sum, count) => sum + count, 0);

  const openPhase = (index: number) =>
    navigate(`${urls.app.sourcingEvents.viewAll}?${bucketQuery(PHASES[index])}`);

  return (
    <PanelCard>
      <PanelHeader
        titleKey="Procurements by phase"
        icon={FiShoppingCart}
        count={total}
        seeAllTo={urls.app.sourcingEvents.viewAll}
      />
      <PanelBody isLoading={isLoading} isError={isError} isEmpty={total === 0} emptyKey="No procurements yet">
        <DoughnutBreakdown
          titleKey="Procurements by phase"
          slices={PHASES.map((phase, index) => ({
            key: phase,
            label: navLabel(BUCKET_META[phase].labelKey),
            colorToken: PHASE_COLOR[phase],
            count: counts[index],
          }))}
          totalLabel={navLabel("Procurements")}
          onSelect={openPhase}
        />
      </PanelBody>
    </PanelCard>
  );
};
